import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface FoodCartItem {
  id: number;
  name: string;
  price: number;
  imageUrl?: string;
  categoryName?: string;
  quantity: number;
}

interface FoodRestaurant {
  id: number;
  name: string;
  deliveryFee?: number;
  deliveryTimeMin?: number;
}

interface FoodCartContextType {
  cart: FoodCartItem[];
  restaurant: FoodRestaurant | null;
  itemCount: number;
  cartTotal: number;
  addItem: (item: Omit<FoodCartItem, 'quantity'>, restaurant: FoodRestaurant) => void;
  removeItem: (itemId: number) => void;
  updateQuantity: (itemId: number, delta: number) => void;
  clearCart: () => void;
  setFullCart: (items: FoodCartItem[], restaurant: FoodRestaurant) => void;
}

const FoodCartContext = createContext<FoodCartContextType | undefined>(undefined);

export const FoodCartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<FoodCartItem[]>(() => {
    try {
      const stored = sessionStorage.getItem('foodCart');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  const [restaurant, setRestaurant] = useState<FoodRestaurant | null>(() => {
    try {
      const stored = sessionStorage.getItem('foodCartRestaurant');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  useEffect(() => {
    sessionStorage.setItem('foodCart', JSON.stringify(cart));
    sessionStorage.setItem('foodCartRestaurant', JSON.stringify(restaurant));
  }, [cart, restaurant]);

  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce((sum, item) => sum + Number(item.price || 0) * item.quantity, 0);

  const addItem = (item: Omit<FoodCartItem, 'quantity'>, rest: FoodRestaurant) => {
    // Se mudou de restaurante, limpar carrinho
    if (restaurant && restaurant.id !== rest.id) {
      setCart([{ ...item, quantity: 1 }]);
    } else {
      setCart(prev => {
        const existing = prev.find(i => i.id === item.id);
        if (existing) {
          return prev.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
        }
        return [...prev, { ...item, quantity: 1 }];
      });
    }
    setRestaurant(rest);
  };

  const removeItem = (itemId: number) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === itemId);
      if (existing && existing.quantity > 1) {
        return prev.map(i => i.id === itemId ? { ...i, quantity: i.quantity - 1 } : i);
      }
      return prev.filter(i => i.id !== itemId);
    });
  };

  const updateQuantity = (itemId: number, delta: number) => {
    setCart(prev =>
      prev.map(i => i.id === itemId ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i)
        .filter(i => i.quantity > 0)
    );
  };

  const clearCart = () => {
    setCart([]);
    setRestaurant(null);
  };

  const setFullCart = (items: FoodCartItem[], rest: FoodRestaurant) => {
    setCart(items);
    setRestaurant(rest);
  };

  return (
    <FoodCartContext.Provider value={{
      cart, restaurant, itemCount, cartTotal,
      addItem, removeItem, updateQuantity, clearCart, setFullCart,
    }}>
      {children}
    </FoodCartContext.Provider>
  );
};

export const useFoodCart = (): FoodCartContextType => {
  const context = useContext(FoodCartContext);
  if (!context) throw new Error('useFoodCart must be used within FoodCartProvider');
  return context;
};
